import { GameEvent, GameState, Player, ScoreEntry } from '../types/game';
import { calculateOvertaking, OVERTAKE_PROJECTION, OVERTAKE_ACTUAL } from './overtake';
import { makeOvertakeEvent, makePenaltyEvent, makeScoreEvent, makeZoneTriggeredEvent } from './events';
import { gameRules } from './gameRules';
import { resolveZoneChain, revealZone, type TrackZone } from './zones';
import { describeZoneChain } from './zoneNarrative';

type FirstRollInfo = {
  rollIndex?: number;
  didBust?: boolean;
  scoringDiceCount?: number;
  bust?: boolean;
};

export interface TurnOutcomeParams {
  state: GameState;
  playerId: string;
  points: number;
  turnForAction: number;
  actionTimestamp: Date;
  lastRollInfo?: FirstRollInfo | null;
  reconstructedFirstRoll?: FirstRollInfo | null;
}

export interface TurnOutcomeResult {
  historyEntry: ScoreEntry | null;
  finalScore: number;
  nextHasFirstEntry: boolean;
  overtakesCount: number;
  newEvents: GameEvent[];
  updatedPlayers: Player[];
  updatedZones?: TrackZone[];
}

const pluralGracz = (count: number, form1 = 'gracza', form2 = 'graczy') => (count === 1 ? form1 : form2);

/**
 * Computes turn outcome (score/penalty/overtake) without mutating reducer state.
 */
export function computeTurnOutcome(params: TurnOutcomeParams): TurnOutcomeResult | null {
  const {
    state,
    playerId,
    points,
    turnForAction,
    actionTimestamp,
    lastRollInfo,
    reconstructedFirstRoll,
  } = params;

  const playerIndex = state.players.findIndex(p => p.id === playerId);
  if (playerIndex === -1) return null;
  const currentPlayer = state.players[playerIndex];
  const updatedPlayers = [...state.players];
  const newEvents: GameEvent[] = [];
  const specialEvents: string[] = [];
  let historyEntry: ScoreEntry | null = null;
  let finalScore = currentPlayer.score;
  let overtakesCount = 0;
  let nextHasFirstEntry = currentPlayer.hasFirstEntry;
  let updatedZones: TrackZone[] | undefined;
  // Ofiary wyprzedzeń z settlementu. Strefy nie mogą ich dorżnąć drugi raz w tej samej
  // turze – oscylujący łańcuch potrafiłby inaczej przejechać po tym samym graczu kilka razy.
  const settlementVictimIds = new Set<string>();

  const zeroDueToFirstRollBust = (
    !!lastRollInfo
    && (state.turnCycle?.rollCount ?? 0) === 1
    && (
      lastRollInfo.didBust === true
      || lastRollInfo.bust === true
      || (lastRollInfo.scoringDiceCount ?? 0) === 0
    )
  ) || (
    !!reconstructedFirstRoll
    && reconstructedFirstRoll.rollIndex === 0
    && (reconstructedFirstRoll.didBust || reconstructedFirstRoll.scoringDiceCount === 0)
  );

  const applyZeroOutcome = (extraDetail?: string) => {
    let penalty = 0;
    const canApplyFirstRollPenalty = zeroDueToFirstRollBust && currentPlayer.score < gameRules.scoring.minScoringDice.thresholdScore;
    if (canApplyFirstRollPenalty) {
      penalty = gameRules.penalties.firstRollBust;
      specialEvents.push(`Kara za pierwszy rzut zero: ${penalty}`);
      newEvents.push(makePenaltyEvent(turnForAction, playerId, penalty, 'PIERWSZA_FURA', currentPlayer.score + penalty));
    } else if (!extraDetail) {
      newEvents.push(makeScoreEvent(turnForAction, playerId, 0, currentPlayer.score + penalty));
    }
    if (extraDetail) specialEvents.push(extraDetail);
    finalScore = currentPlayer.score + penalty;
    nextHasFirstEntry = currentPlayer.hasFirstEntry;
    const details = specialEvents.filter(Boolean).join(' + ');
    historyEntry = {
      playerId,
      playerName: currentPlayer.name,
      points: finalScore - currentPlayer.score,
      newScore: finalScore,
      turn: turnForAction,
      timestamp: actionTimestamp,
      specialEvent: penalty !== 0 ? 'penalty' : undefined,
      details: details || undefined,
    };
  };

  if (points === 0) {
    applyZeroOutcome();
  } else if (!currentPlayer.hasFirstEntry && points < gameRules.scoring.firstEntryMin) {
    // Pierwsze wejście: zapis poniżej progu nie wchodzi na tablicę. Klient blokuje bank
    // (`getBankConstraint`, kind `firstEntryTooLow`), ale rozliczenie musi egzekwować to
    // samodzielnie – inaczej wystarczy rozjechany klient, żeby wejść za 10 punktów.
    // Serwer trzyma tę samą regułę (`computeTurnEnd`, `forcedZeroReason`).
    specialEvents.push(`Pierwszy wpis poniżej ${gameRules.scoring.firstEntryMin} - tura stracona`);
    applyZeroOutcome();
  } else {
    const baseScore = currentPlayer.score + points;
      const potentialOvertake =
      currentPlayer.score >= gameRules.overtake.protectedScore
        ? null
        : calculateOvertaking(state.players, playerId, baseScore, OVERTAKE_PROJECTION);
    // UWAGA: projekcja przekroczenia 1000 celowo IGNORUJE strefy. Reguła "dokładnie 1000"
    // musi dać się policzyć z informacji widocznych dla gracza – ukryta strefa nigdy nie
    // unieważnia tury, którą gracz uznał za legalną. Nie "naprawiaj" tego.
    const projectedWithPotential = baseScore + (potentialOvertake?.bonusPoints ?? 0);
    if (projectedWithPotential > gameRules.scoring.winScore) {
      applyZeroOutcome(`Wynik powyżej ${gameRules.scoring.winScore} - tura stracona`);
    } else {
      const hasOvertakeThisTurn = (state.events || []).some(
        (ev) => ev.type === 'OVERTAKE' && ev.turn === turnForAction && ev.payload.overtakerId === playerId
      );
      const overtakeResult = hasOvertakeThisTurn
        ? null
        : calculateOvertaking(state.players, playerId, baseScore, OVERTAKE_ACTUAL);
      const overtakeBonus = overtakeResult ? overtakeResult.bonusPoints : 0;
      finalScore = baseScore + overtakeBonus;
      nextHasFirstEntry = nextHasFirstEntry || baseScore >= gameRules.scoring.firstEntryMin || points >= gameRules.scoring.firstEntryMin;
      newEvents.push(makeScoreEvent(turnForAction, playerId, points, baseScore));

      if (overtakeResult && overtakeResult.overtaken.length > 0) {
        overtakesCount = overtakeResult.overtaken.length;
        const penaltiesPayload: { playerId: string; delta: number }[] = [];
        overtakeResult.overtaken.forEach(victim => {
          settlementVictimIds.add(victim.id);
          const victimIndex = updatedPlayers.findIndex(p => p.id === victim.id);
          if (victimIndex !== -1) {
            updatedPlayers[victimIndex] = {
              ...updatedPlayers[victimIndex],
              score: updatedPlayers[victimIndex].score + overtakeResult.penaltyPoints,
            };
          }
          penaltiesPayload.push({ playerId: victim.id, delta: overtakeResult.penaltyPoints });
        });
        newEvents.push(makeOvertakeEvent(
          turnForAction,
          playerId,
          overtakeResult.bonusPoints,
          penaltiesPayload,
          overtakeResult.stages?.map(s => ({
            players: s.players.map(p => p.id),
            cumulativeBonus: s.cumulativeBonus,
            resultingScore: s.resultingScore,
          })),
        ));
        const allNames = overtakeResult.overtaken.map(p => p.name).join(', ');
        specialEvents.push(`Wyprzedzono ${overtakesCount} ${pluralGracz(overtakesCount)} (${allNames}): +${overtakeResult.bonusPoints}`);
      }

      // Strefy: łańcuch rozstrzygany na wyniku spoczynkowym (po zapisie punktów
      // i wyprzedzeniach). Każde wejście w strefę wywołuje efekt; szczegóły – łącznie
      // z wykrywaniem pętli – siedzą w `resolveZoneChain`, wspólnym z serwerem.
      const chain = resolveZoneChain({
        zones: state.zones,
        players: updatedPlayers,
        playerId,
        startScore: finalScore,
        hasFirstEntry: nextHasFirstEntry,
        excludedVictimIds: settlementVictimIds,
      });
      if (chain) {
        chain.penalties.forEach(({ playerId: victimId, delta }) => {
          const victimIndex = updatedPlayers.findIndex(p => p.id === victimId);
          if (victimIndex !== -1) {
            updatedPlayers[victimIndex] = {
              ...updatedPlayers[victimIndex],
              score: updatedPlayers[victimIndex].score + delta,
            };
          }
        });
        finalScore = chain.finalScore;
        overtakesCount += chain.penalties.length;
        updatedZones = chain.visitedZones.reduce(
          (zones, zone) => revealZone(zones, zone),
          state.zones,
        );
        newEvents.push(makeZoneTriggeredEvent(turnForAction, playerId, chain));
        const zoneEvent = newEvents[newEvents.length - 1];
        if (zoneEvent.type === 'ZONE_TRIGGERED') {
          specialEvents.push(...describeZoneChain(
            zoneEvent.payload,
            (victimId) => state.players.find(p => p.id === victimId)?.name ?? '',
          ));
        }
      }

      const details = specialEvents.filter(Boolean).join(', ');
      historyEntry = {
        playerId,
        playerName: currentPlayer.name,
        points: finalScore - currentPlayer.score,
        newScore: finalScore,
        turn: turnForAction,
        timestamp: actionTimestamp,
        specialEvent: overtakesCount > 0 ? 'overtake' : undefined,
        details: details || undefined,
      };
    }
  }

  return {
    historyEntry,
    finalScore,
    nextHasFirstEntry,
    overtakesCount,
    newEvents,
    updatedPlayers,
    updatedZones,
  };
}
